/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import React, {
  ChangeEvent,
  useCallback,
  useMemo,
  useRef,
  useState
} from 'react';
import {ClickAwayListener, makeStyles, Paper} from '@material-ui/core';
import MenuList from '@material-ui/core/MenuList';
import Popper from '@material-ui/core/Popper';
import {translate} from '@ion/core/src/locale/locale';
import {
  GenericObject,
  PlatformDeviceType,
  PlatformDeviceTypes,
  PlatformFileType
} from '@ion/core/src/types/entities';
import TextField from '@ion/dls/src/components/textfield/TextField';
import {DynamicTreeView} from '@ion/dls/src/components/treeviews/DynamicTreeView';
import {TreeViewDataItem} from '@ion/dls/src/components/treeviews/TreeViews';
import {DropdownArrowIcon} from '@ion/dls/src/icons/DropdownArrowIcon';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';
import {DynamicDataItem} from '@ion/features/src/search/types';
import {filterDynamicDataList, transformDeviceTypesToTree} from '@ion/features/src/search/utils';

// defines the device/file types chooser prop definitions ...
export type DeviceFileTypeChooserProps = {
  onChange: Function;
  deviceTypes: PlatformDeviceTypes;
  selectedType?: string;
};

const MenuListID       = 'suggestion-menu';
const DeviceFileTypeID = 'device-file-type';

const useStyles = makeStyles((theme: DLSTheme) => ({
  searchSuggestionInputRoot : {
    height : '55px'
  },
  searchSuggestionInputUnderline : {
    paddingBottom : '0px !important',
    height        : 'inherit',
    '&::before'   : {
      borderBottom : `1px solid ${dlsColor('grey4')} !important`
    },
    '&.Mui-focused' : {
      '&::before' : {
        borderBottom : `1px solid ${dlsColor('aaBlue')} !important`
      }
    }
  },
  suggestionsWrapper : {
    zIndex   : 5,
    position : 'relative',
    border   : '1px solid #e0e0e0',
    maxWidth : 500,
    width    : '100%'
  },
  searchIcon : {
    display        : 'flex',
    justifyContent : 'center',
    alignItems     : 'center',
    cursor         : 'pointer'
  },
  listWrapper : {
    height    : 'auto',
    maxHeight : '400px',
    overflow  : 'scroll'
  },
  input : {
    fontSize    : 14,
    fontFamily  : theme.typography.fontFamily,
    paddingLeft : '7px'
  }
}));

/**
 * The DeviceFileTypeChooser provides a list of device and file types for the current account
 * and allows the user to single-select a device and file type from the list.
 *
 * ```typescript
 *
 *     import {DeviceFileTypeChooser} from './DeviceFileTypeChooser';
 *
 *     <DeviceFileTypeChooser deviceTypes={deviceTypes}
 *      onChange={handleDeviceType} />
 *
 * ```
 */
export const DeviceFileTypeChooser = ({
  deviceTypes,
  selectedType,
  onChange
}: DeviceFileTypeChooserProps) => {
  const AllText                                 = translate('admin-devices:view.files.all');
  const classes                                 = useStyles();
  const queryInputRef                           = useRef(null);
  const menuListRef                             = useRef(null);
  const [dropdownOpen, setDropdownOpen]         = useState(false);
  const [searchQuery, setSearchQuery]           = useState(AllText);
  const [filteredTreeData, setFilteredTreeData] = useState<DynamicDataItem[]>(null);
  const AllTextOption = {
    name             : AllText,
    searchableString : AllText,
    children         : [{name: AllText}]
  };

  const deviceTypesTree            = useMemo(() => transformDeviceTypesToTree(deviceTypes), [deviceTypes]);
  const deviceTypesTreeWithAllText = useMemo(() => (
    deviceTypesTree?.length > 1 ? [AllTextOption, ...deviceTypesTree] : [...deviceTypesTree]
  ), [deviceTypesTree]);

  const onTreeViewLabelSelect = useCallback(function(item: TreeViewDataItem) {
    onMenuSelection(item);
  }, []);

  const onMenuSelection = useCallback(function(menuItem: GenericObject) {
    const {fullName, fileTypes, deviceType, platformId, name, children} = menuItem;
    if (name && name === AllText) {
      setDropdownOpen(false);
      setSearchQuery(name);
      onChange({
        deviceTypeOption : '',
        fileTypeOption   : ''
      });
      return;
    }
    const deviceTypeOption = {
      name      : deviceType ?? name,
      fileTypes : fileTypes ?? children[0]?.fileTypes
    } as PlatformDeviceType;
    const fileTypeOption = fileTypes?.find(
      fileType => fileType.platformId === platformId
    ) as PlatformFileType;
    setDropdownOpen(false);
    setSearchQuery(fullName);
    onChange({
      deviceTypeOption,
      fileTypeOption
    });
  }, []);

  const handleClose = useCallback(() => () => {
    setDropdownOpen(false);
  }, []);

  const onInputChange = useCallback(({currentTarget, target}: ChangeEvent<HTMLInputElement>) => {
    const {value} = target;
    if (value) {
      setFilteredTreeData(filterDynamicDataList(deviceTypesTreeWithAllText, value, -1, 'searchableString'));
    } else {
      setFilteredTreeData(null);
      onChange({
        deviceTypeOption : '',
        fileTypeOption   : ''
      });
    }
    setSearchQuery(value);
    setDropdownOpen(true);
  }, [deviceTypes]);

  return (
    <ClickAwayListener onClickAway={handleClose()}>
      <div>
        <TextField id={DeviceFileTypeID}
          classes={{root: classes.searchSuggestionInputRoot}}
          autoComplete={'off'}
          placeholder={translate('admin-devices:view.files.searchDeviceFileType')}
          fieldLabel={translate('admin-devices:view.files.selectDeviceFileType')}
          inputRef={queryInputRef}
          onChange={onInputChange}
          value={searchQuery}
          aria-controls={dropdownOpen ? 'menu-list-grow' : undefined}
          aria-haspopup='true'
          InputProps={{
            classes : {
              input     : classes.input,
              underline : classes.searchSuggestionInputUnderline
            },
            endAdornment : (<div className={classes.searchIcon}
              onClick={function() {
                setDropdownOpen(true);
              }}>
              <DropdownArrowIcon />
            </div>)
          }}/>
        <Popper open={dropdownOpen}
          className={classes.suggestionsWrapper}
          anchorEl={dropdownOpen ? queryInputRef.current : null}
          role={undefined}
          transition
          disablePortal
          style={{}}>
          <Paper>
            {dropdownOpen && (
              <MenuList id={MenuListID}
                ref={menuListRef}
                variant={'menu'}
                classes={{root: classes.listWrapper}}>
                <DynamicTreeView parentClick={true}
                  data={filteredTreeData ?? deviceTypesTreeWithAllText}
                  onItemClick={onTreeViewLabelSelect} />
              </MenuList>
            )}
          </Paper>
        </Popper>
      </div>
    </ClickAwayListener>
  );
};

export default DeviceFileTypeChooser;
